import { ApiError, BaseProvider } from "./base.provider";

export interface AnnouncementTranslation {
  title: string;
  body: string;
  cta_label: string;
}

export interface Announcement {
  id: string;
  slug: string;
  translations: Record<string, AnnouncementTranslation>;
  label: string;
  icon_name: string;
  accent_color: string;
  redirect_path: string;
  image_url: string | null;
  priority: number;
  is_enabled: boolean;
  starts_at: string;
  ends_at: string | null;
  timezones: string[] | null;
  feature_flag_key: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementPayload {
  slug: string;
  translations: Record<string, AnnouncementTranslation>;
  label?: string;
  icon_name?: string;
  accent_color?: string;
  redirect_path?: string;
  image_url?: string | null;
  priority?: number;
  starts_at: string;
  ends_at?: string | null;
  timezones?: string[] | null;
  feature_flag_key?: string | null;
}

export interface Locale {
  tag: string;
  native_name: string;
  is_default: boolean;
}

export interface AnnouncementValidationError {
  field: string;
  error: string;
}

// The only endpoint that returns a JSON body on 400 — every other error is a
// bare status, so this is safe to attempt and fall back from.
export function parseAnnouncementValidationError(
  err: unknown,
): AnnouncementValidationError | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  try {
    const parsed = JSON.parse(err.message);
    if (typeof parsed?.field === "string" && typeof parsed?.error === "string") {
      return parsed as AnnouncementValidationError;
    }
    return null;
  } catch {
    return null;
  }
}

class AnnouncementsProvider extends BaseProvider {
  list(): Promise<Announcement[]> {
    return this.request<Announcement[]>("/v1/admin/announcements");
  }

  create(payload: AnnouncementPayload): Promise<Announcement> {
    return this.request<Announcement>("/v1/admin/announcements", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  update(id: string, payload: AnnouncementPayload): Promise<Announcement> {
    return this.request<Announcement>(`/v1/admin/announcements/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  setEnabled(id: string, isEnabled: boolean): Promise<void> {
    return this.request<void>(`/v1/admin/announcements/${id}/enabled`, {
      method: "PATCH",
      body: JSON.stringify({ is_enabled: isEnabled }),
    });
  }

  delete(id: string): Promise<void> {
    return this.request<void>(`/v1/admin/announcements/${id}`, { method: "DELETE" });
  }

  listLocales(): Promise<Locale[]> {
    return this.request<Locale[]>("/v1/locales");
  }
}

export const announcementsProvider = new AnnouncementsProvider();
